import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type ReactNode, useEffect, useSyncExternalStore } from 'react'
import { requetes } from '../api'
import { Icone } from './Icone'

// Après un rattachement, la pièce est placée puis rangée par le worker en arrière-plan : ce
// suivi, en bas de l'écran, montre où elle est allée (ou qu'elle attend d'être classée) pendant
// qu'on enchaîne les tâches. Gardé pour l'onglet (sessionStorage), cinq pièces au plus.

const CLE = 'suivi-rangement'
const MAX = 5
const ecouteurs = new Set<() => void>()

function lireIds(): string[] {
  try {
    const brut = sessionStorage.getItem(CLE)
    return brut ? (JSON.parse(brut) as string[]) : []
  } catch {
    return []
  }
}

let ids = lireIds()

function ecrire(suivants: string[]) {
  ids = suivants
  try {
    sessionStorage.setItem(CLE, JSON.stringify(ids))
  } catch {
    // Stockage indisponible (navigation privée) : le suivi vit le temps de la page.
  }
  for (const f of ecouteurs) f()
}

/** Suivre le rangement d'une pièce qu'on vient de rattacher. */
export function suivreRangement(pieceId: string) {
  ecrire([pieceId, ...ids.filter((x) => x !== pieceId)].slice(0, MAX))
}

const oublier = (pieceId: string) => ecrire(ids.filter((x) => x !== pieceId))

function useSuivis() {
  return useSyncExternalStore(
    (f) => {
      ecouteurs.add(f)
      return () => ecouteurs.delete(f)
    },
    () => ids,
  )
}

/** Chemin lisible : ce qui suit la racine « Classement … », sinon le chemin entier. */
const cheminCourt = (chemin: string) => chemin.replace(/^.*?Classement [^/]+\//, '')

export function SuiviRangement() {
  const suivis = useSuivis()
  if (suivis.length === 0) return null
  return (
    <aside className="suivi-rangement" aria-live="polite" aria-label="Rangement des pièces">
      {suivis.map((id) => (
        <LigneSuivi key={id} id={id} />
      ))}
    </aside>
  )
}

function LigneSuivi({ id }: { id: string }) {
  const piece = useQuery({
    ...requetes.piece(id),
    // Tant que le placement ou le rangement tournent côté worker, on relit.
    refetchInterval: (q) => {
      const p = q.state.data
      if (!p) return 4_000
      return p.rangement === 'a_faire' ? 4_000 : false
    },
  })
  const p = piece.data
  // Pièce supprimée ou hors périmètre : le suivi s'efface.
  useEffect(() => {
    if (piece.isError) oublier(id)
  }, [piece.isError, id])
  if (piece.isError) return null
  const nom = p ? (p.emetteur ?? p.extraction?.emetteur.nom ?? p.nomFichier ?? 'Pièce') : '…'
  let etat: ReactNode
  if (!p || p.rangement === 'a_faire') {
    etat = <span className="discret">Classement en cours…</span>
  } else if (p.rangement === null) {
    etat = <span className="discret">Rattachée, sans rangement dans le Drive.</span>
  } else if (p.rangement === 'fait') {
    etat = (
      <>
        <span>
          {p.cheminDrive ? (
            <>
              Rangée dans <strong>{cheminCourt(p.cheminDrive)}</strong>
            </>
          ) : (
            'Rangée.'
          )}
        </span>
        {p.lienDrive && (
          <a href={p.lienDrive} target="_blank" rel="noreferrer" className="suivi-lien">
            Ouvrir dans le Drive
          </a>
        )}
      </>
    )
  } else {
    etat = (
      <span className="suivi-attente">
        À classer : {p.rangementErreur ?? 'dossier à choisir.'}{' '}
        <Link to="/pieces/$id" params={{ id }}>
          Choisir le dossier
        </Link>
      </span>
    )
  }
  return (
    <div className="suivi-ligne carte">
      <div className="suivi-texte">
        <Link to="/pieces/$id" params={{ id }} className="suivi-nom">
          {nom}
          {p?.entite ? <span className="discret"> · {p.entite}</span> : null}
        </Link>
        {etat}
      </div>
      <button
        type="button"
        className="bouton-icone"
        aria-label="Masquer ce suivi"
        onClick={() => oublier(id)}
      >
        <Icone nom="close" taille={16} />
      </button>
    </div>
  )
}
